import type { ReactNode } from "react";

import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";

interface ICompareCardHeaderProps {
    title: string;
    tooltipText: string;
    dateRangeText: string;
    countryCode: number;
    countryText: string;
    dataAutomation: string;
    titleAccessory?: ReactNode;
    headerAction?: ReactNode;
    additionalQueryContent?: ReactNode;
}

export const CompareCardHeader = ({
    title,
    tooltipText,
    dateRangeText,
    countryCode,
    countryText,
    dataAutomation,
    titleAccessory,
    headerAction,
    additionalQueryContent,
}: ICompareCardHeaderProps) => {
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger(dataAutomation);

    return (
        <Card.Header withDivider>
            <Card.Title>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        data-automation={`${dataAutomation}-title`}
                    >
                        {title}
                    </Text>
                    {titleAccessory ?? (
                        <Tooltip content={tooltipText}>
                            <Info24px
                                size={24}
                                color={colorsPalettes.carbon[200]}
                            />
                        </Tooltip>
                    )}
                </Flex>
                <Card.Query>
                    {additionalQueryContent ?? (
                        <>
                            <Card.QueryItem
                                iconName="daily-ranking"
                                data-automation={`${dataAutomation}-date-range`}
                                {...durationTriggerProps}
                            >
                                {dateRangeText}
                            </Card.QueryItem>
                            <Flex
                                gap="8px"
                                alignItems="center"
                                data-automation={`${dataAutomation}-country`}
                                {...countryTriggerProps}
                            >
                                <SWReactCountryIcons
                                    countryCode={countryCode}
                                    size="xs"
                                />
                                <Text
                                    fontVariant="chip1"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {countryText}
                                </Text>
                            </Flex>
                        </>
                    )}
                </Card.Query>
            </Card.Title>
            {headerAction}
        </Card.Header>
    );
};
